/**
 * What sits above the compact composer: lightweight live status, then the
 * reply or pending question when the turn stops (RFC 0304 3h).
 *
 * Reads the session itself, the same live document the thread reads, so the
 * strip updates exactly as the full view does. Only the session's small header
 * and the one message it points at are projected, never the whole transcript.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo'
import {
  parseQuestionBody,
  projectSessionStripActivity,
  type QuestionBody,
  registryStatus,
  type SessionRunStatus,
  type SessionStripActivity,
} from '@mod/core/model'
import { useContext, useEffect, useRef, useState } from 'react'
import { useDocument } from '../../services/world/repo/useDocument'
import { StatusDot } from '../../shell/window/StatusDot'
import { ComposerEngaged } from './Composer'
import { Markdown } from './Markdown'
import { pickLabel, type StripTaps } from './stripQuestion'
import { useQuestionTaps } from './useQuestionTaps'

interface ProjectSessionStatus {
  sessionMeta?: Record<string, { status?: unknown; statusAt?: unknown }>
}

export function compactActivityText(
  status: SessionRunStatus | undefined,
): string | null {
  switch (status) {
    case 'queued':
      return 'waiting for a worker'
    case 'running':
      return 'working'
    case 'asking':
      return 'needs your answer'
    default:
      return null
  }
}

export type CompactLiveActivity = {
  kind: 'waiting' | 'reading' | 'tool' | 'text' | 'asking'
  text: string
}

/**
 * The live line, held steady between steps (fix: the flickering line).
 *
 * Each step of a run starts a fresh assistant message, and for the moment
 * before its first tool call or word that message says nothing, so the line
 * fell back to a bare "working" between every pair of back-to-back tool calls:
 * "Read a.ts", "working", "Read b.ts", in two fonts. While the run goes on,
 * the last real step stands until the next one replaces it. A run that stops,
 * waits or asks lets it go.
 */
export function steadyLive(
  previous: CompactLiveActivity | null,
  next: CompactLiveActivity | null,
): CompactLiveActivity | null {
  if (next?.kind !== 'reading') return next
  if (previous?.kind === 'tool' || previous?.kind === 'text') return previous
  return next
}

/** Live state wins over the previous turn's cached reply. */
export function compactLiveActivity(
  status: SessionRunStatus | undefined,
  cached: SessionStripActivity | undefined,
): CompactLiveActivity | null {
  if (status === 'queued')
    return { kind: 'waiting', text: 'waiting for a worker' }
  if (status === 'asking') return { kind: 'asking', text: 'needs your answer' }
  if (status !== 'running') return null
  if (cached?.kind === 'tool') {
    return {
      kind: 'tool',
      text: cached.summary ? `${cached.name}  ${cached.summary}` : cached.name,
    }
  }
  if (cached?.kind === 'text') return { kind: 'text', text: cached.text }
  return { kind: 'reading', text: 'working' }
}

function firstLine(text: string): string {
  return (
    text
      .split('\n')
      .map((line) => line.trim())
      .find(Boolean) ?? ''
  )
}

/** The pending question as taps, without reading its source session. */
export function QuestionTaps({
  body,
  taps,
  onTap,
  onSkip,
}: {
  body: QuestionBody
  taps: StripTaps
  /** An option value, or null for the Next/Send button. */
  onTap: (value: string | null) => void
  /** Decline the whole ask, as the thread's form does. */
  onSkip?: () => void
}) {
  const step = taps.step
  const question = body.questions[step]
  if (!question) return null
  const state = taps.selection[question.id] ?? { values: [], freeText: '' }
  const last = step === body.questions.length - 1

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="text-foreground">
        {body.questions.length > 1 ? (
          <span className="mr-1.5 text-muted-foreground">
            {step + 1}/{body.questions.length}
          </span>
        ) : null}
        {question.prompt}
      </div>
      <div className="flex flex-col gap-1">
        {question.options.map((option) => {
          const { label, recommended } = pickLabel(option.label)
          const selected = state.values.includes(option.value)
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onTap(option.value)}
              aria-pressed={question.multiSelect ? selected : undefined}
              title={recommended ? "The agent's pick" : undefined}
              className={`w-full rounded-md border px-2 py-1 text-left ${
                selected
                  ? 'border-blue-500 bg-blue-500/[0.12] text-foreground'
                  : recommended
                    ? 'border-blue-500/60 bg-blue-500/[0.06] text-foreground hover:bg-blue-500/[0.12]'
                    : 'border-border text-foreground hover:border-blue-500 hover:bg-blue-500/[0.06]'
              }`}
            >
              <span className="font-medium">{label}</span>
              {recommended ? (
                <span className="ml-1.5 text-[10px] text-blue-500 uppercase tracking-wide">
                  pick
                </span>
              ) : null}
              {option.description ? (
                <span className="text-muted-foreground">
                  {' '}
                  - {option.description}
                </span>
              ) : null}
            </button>
          )
        })}
        {question.multiSelect || onSkip ? (
          <div className="flex items-center gap-2">
            {question.multiSelect ? (
              <button
                type="button"
                disabled={state.values.length === 0}
                onClick={() => onTap(null)}
                className="rounded-md bg-foreground/[0.08] px-2 py-0.5 text-foreground hover:bg-foreground/[0.14] disabled:opacity-40"
              >
                {last ? 'Send' : 'Next'}
              </button>
            ) : null}
            {/* The strip is the only place the ask is drawn when the thread is
                collapsed, so it has to be declinable here too. Without it the
                session sits asking until someone opens the thread to say no. */}
            {onSkip ? (
              <button
                type="button"
                onClick={onSkip}
                className="ml-auto rounded-md px-2 py-0.5 text-muted-foreground hover:text-foreground"
              >
                Skip
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export function SessionActivity({
  sessionId,
  projectId,
}: {
  sessionId: string
  projectId: string
}) {
  // CHEAP READS ONLY: this renders on every streamed change. The status comes
  // from the project list, which every window already holds; the session is
  // read for its small header and the one message it points at. Nothing here
  // decodes the session's whole log.
  const [project] = useDocument<ProjectSessionStatus>(
    projectId as AnyDocumentId,
  )
  const [sessionDoc] = useDocument<unknown>(sessionId as AnyDocumentId)
  const asked = sessionDoc
    ? projectSessionStripActivity(sessionDoc, 'asking')
    : null
  const status: SessionRunStatus | undefined =
    asked?.kind === 'question'
      ? 'asking'
      : registryStatus(project?.sessionMeta?.[sessionId], Date.now())
  const cached =
    asked?.kind === 'question'
      ? asked
      : sessionDoc && status
        ? (projectSessionStripActivity(sessionDoc, status) ?? undefined)
        : undefined
  const reply = cached?.kind === 'reply' ? cached : undefined
  const question =
    cached?.kind === 'question'
      ? {
          interactionId: cached.interactionId,
          body: parseQuestionBody(cached.bodyJson),
        }
      : undefined
  // Held per session: a step from one session never stands in for another's.
  const lastStep = useRef<{ id: string; live: CompactLiveActivity } | null>(
    null,
  )
  const fresh = compactLiveActivity(status, cached)
  const live = steadyLive(
    lastStep.current?.id === sessionId ? lastStep.current.live : null,
    fresh,
  )
  lastStep.current =
    live && status === 'running' ? { id: sessionId, live } : null

  // Shared with the World view's lines, so a tap means one thing everywhere.
  const { taps, onTap, onSkip } = useQuestionTaps(
    sessionId,
    projectId,
    question,
  )

  const engaged = useContext(ComposerEngaged)
  const replyText = reply?.text ?? ''
  const [seen, setSeen] = useState<string | null>(null)
  useEffect(() => {
    if (engaged) setSeen(replyText)
  }, [engaged, replyText])
  const folded = !engaged && seen === replyText

  if (question || (!live && reply)) {
    if (folded) {
      const line = question
        ? (question.body.questions[taps.step]?.prompt ?? '')
        : firstLine(replyText)
      return (
        <div className="flex w-full min-w-0 items-center gap-2 px-1 pb-1.5 text-xs">
          <span
            className="min-w-0 flex-1 truncate text-foreground/80"
            title={line}
          >
            {line}
          </span>
        </div>
      )
    }
    return (
      <div className="flex w-full min-w-0 items-start gap-2 px-1 pb-1.5 text-xs">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          {replyText ? (
            <div className="scroll-quiet min-w-0 max-h-[45vh] overflow-y-auto text-foreground/90">
              <Markdown text={replyText} />
            </div>
          ) : null}
          {question && question.body.questions.length > 0 ? (
            <QuestionTaps
              body={question.body}
              taps={taps}
              onTap={onTap}
              onSkip={onSkip}
            />
          ) : null}
        </div>
      </div>
    )
  }

  if (!live) return null
  return <LiveRow live={live} />
}

/** The live line: a dot for the state, and the step the agent is on. */
function LiveRow({ live }: { live: CompactLiveActivity }) {
  return (
    <div className="flex w-full min-w-0 items-center gap-2 px-1 pb-1.5 text-muted-foreground text-xs">
      <StatusDot
        status={
          live.kind === 'waiting'
            ? 'queued'
            : live.kind === 'asking'
              ? 'asking'
              : 'working'
        }
      />
      <span
        className={`min-w-0 flex-1 truncate ${live.kind === 'tool' ? 'font-mono' : ''}`}
        title={live.text}
      >
        {live.text}
      </span>
    </div>
  )
}

/**
 * The live line alone, for a session whose status is known some other way
 * than a project's registry: the World agent's, which is in no project, so the
 * World view reads its run off its own log. Its reply and questions are not
 * drawn here; the World view's thread already shows them.
 */
export function SessionLiveLine({
  sessionId,
  sessionDoc,
  status,
}: {
  sessionId: string
  sessionDoc: unknown
  status: SessionRunStatus | undefined
}) {
  const cached =
    sessionDoc && status
      ? (projectSessionStripActivity(sessionDoc, status) ?? undefined)
      : undefined
  const lastStep = useRef<{ id: string; live: CompactLiveActivity } | null>(
    null,
  )
  const live = steadyLive(
    lastStep.current?.id === sessionId ? lastStep.current.live : null,
    compactLiveActivity(status, cached),
  )
  lastStep.current =
    live && status === 'running' ? { id: sessionId, live } : null
  if (!live) return null
  return <LiveRow live={live} />
}
